import {
  parsePhoneNumberFromString,
  formatIncompletePhoneNumber,
} from "libphonenumber-js/max";
import {
  DEFAULT_COUNTRY,
  getCountryConfig,
  isSupportedCountry,
  resolveCountry,
  resolveCompanyCountry,
} from "./countryConfig";
import { isValidNumberForRegion } from "./phoneRegionValidation";

export const PHONE_EXACTLY_TEN_MSG = "Phone number must be exactly 10 digits.";

/** Strip non-digits and cap length (default 10). */
export function digitsOnlyPhone(value, maxLen = 10) {
  return String(value ?? "").replace(/\D/g, "").slice(0, maxLen);
}

/**
 * National storage — strips calling-code prefixes when country is known.
 * Legacy (no country): strips +1 / +91 and caps at 10 digits (unchanged US behavior).
 */
export function nationalPhoneForStorage(phone, country) {
  const d = String(phone ?? "").replace(/\D/g, "");
  if (!d) return "";

  if (country && isSupportedCountry(country)) {
    const config = getCountryConfig(country);
    const dialCode = config.phoneCountryCode.replace("+", "");
    let national = d;
    if (national.startsWith(dialCode) && national.length > dialCode.length) {
      national = national.slice(dialCode.length);
    }
    if (national.startsWith("0")) {
      national = national.replace(/^0+/, "");
    }
    return national.slice(0, config.nationalMaxLength);
  }

  // Legacy path — preserve existing US/India behavior
  if (d.length === 11 && d[0] === "1") return d.slice(1);
  if (d.length === 12 && d.startsWith("91")) return d.slice(2);
  if (d.length > 10) return d.slice(-10);
  return d;
}

/**
 * Build E.164 from stored fields. Twilio / Firebase Auth entry point.
 */
export function phoneToE164({ phoneCountryCode, phoneNumber, country } = {}) {
  const resolvedCountry = country && isSupportedCountry(country) ? country : DEFAULT_COUNTRY;
  const config = getCountryConfig(resolvedCountry);
  const code = (phoneCountryCode || config.phoneCountryCode).replace(/\D/g, "");
  const national = nationalPhoneForStorage(phoneNumber, resolvedCountry);
  if (!national) return "";
  return `+${code}${national}`;
}

/** E.164 when libphonenumber validates, or when our country rules accept the national number. */
export function phoneToValidE164({ phoneCountryCode, phoneNumber, country } = {}) {
  const e164 = phoneToE164({ phoneCountryCode, phoneNumber, country });
  if (!e164) return "";
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed?.isValid()) return e164;
  const resolved = country && isSupportedCountry(country) ? country : DEFAULT_COUNTRY;
  if (isValidPhoneForCountry(phoneNumber, resolved)) return e164;
  return "";
}

/**
 * Resolve phone fields from a record (handles legacy US-only docs).
 */
export function resolvePhoneFields(record = {}, company = {}) {
  const country = resolveCountry(record, company);
  const config = getCountryConfig(country);
  const phoneCountryCode =
    record.phoneCountryCode === config.phoneCountryCode
      ? record.phoneCountryCode
      : company.phoneCountryCode === config.phoneCountryCode
        ? company.phoneCountryCode
        : config.phoneCountryCode;
  const phoneNumber = nationalPhoneForStorage(
    record.phoneNumber ?? record.mobileNumber ?? record.phone ?? "",
    country
  );
  return { country, phoneCountryCode, phoneNumber };
}

export function isEmployeeUser(record = {}) {
  const role = record?.role;
  if (Array.isArray(role)) return role.includes("Employee");
  return role === "Employee";
}

/** Employees: own country/code first, then company. Other roles: own record only. */
export function resolveEmployeePhoneFields(record = {}, company = {}) {
  if (!isEmployeeUser(record)) {
    return resolvePhoneFields(record);
  }
  return resolvePhoneFields(record, company);
}

/**
 * Normalize phone fields before Firestore/API writes.
 */
export function normalizePhoneFields(fields = {}, companyCountry) {
  const country = resolveCountry(fields, { country: companyCountry });
  const config = getCountryConfig(country);
  const phoneCountryCode =
    fields.phoneCountryCode === config.phoneCountryCode
      ? fields.phoneCountryCode
      : config.phoneCountryCode;
  return {
    country,
    phoneCountryCode,
    phoneNumber: nationalPhoneForStorage(fields.phoneNumber ?? fields.mobileNumber, country),
  };
}

/**
 * Normalize and validate phone fields before Firestore/API writes.
 * @returns {{ ok: boolean, error?: string, fields: ReturnType<typeof normalizePhoneFields> }}
 */
export function validateAndNormalizePhone(
  input = {},
  companyCountry,
  { allowEmpty = false, skipValidation = false } = {}
) {
  const fields = normalizePhoneFields(input, companyCountry);
  if (skipValidation) {
    return { ok: true, fields };
  }
  if (!fields.phoneNumber) {
    if (allowEmpty) return { ok: true, fields };
    return { ok: false, error: "Mobile is Required", fields };
  }
  if (!isValidPhoneForCountry(fields.phoneNumber, fields.country)) {
    return { ok: false, error: getInvalidPhoneMessage(fields.country), fields };
  }
  return { ok: true, fields };
}

/**
 * National 10-digit phone validation (legacy).
 * @param {string} number
 * @param {{ allowEmpty?: boolean }} opts
 */
export function validateTenDigitMobile(number, { allowEmpty = true } = {}) {
  const digits = digitsOnlyPhone(number, 10);
  if (!digits) {
    return allowEmpty ? "" : "Mobile is Required";
  }
  if (!/^\d{10}$/.test(digits)) {
    return PHONE_EXACTLY_TEN_MSG;
  }
  return "";
}

export const INVALID_NANP_MOBILE_MSG =
  "Enter a valid US, Canada, or other +1 area mobile number.";

export const INVALID_STORED_SMS_MOBILE_MSG =
  "Enter a valid US/Canada/+1 or India mobile number.";

export function getInvalidPhoneMessage(country = DEFAULT_COUNTRY) {
  const config = getCountryConfig(country);
  if (country === "US" || country === "CA") {
    return `Enter a valid ${config.name} phone number. This number appears to belong to another country.`;
  }
  return `Enter a valid ${config.name} phone number.`;
}

/** NANP national number: NXX-NXX-XXXX (area/exchange cannot start with 0 or 1). */
function isBasicNanpTenDigit(national) {
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(String(national ?? ""));
}

/** India mobile numbers are 10 digits starting with 6–9. */
function isIndianMobilePattern(national) {
  return /^[6-9]\d{9}$/.test(String(national ?? ""));
}

/** +44 territories accepted when United Kingdom is the company country. */
const GB_RELATED_REGIONS = new Set(["GB", "GG", "IM", "JE"]);

function isValidNanpForSelectedCountry(national, selectedCountry) {
  if (!isBasicNanpTenDigit(national)) return false;

  const usCheck = isValidNumberForRegion(national, "US");
  const caCheck = isValidNumberForRegion(national, "CA");

  if (selectedCountry === "US" && caCheck.valid && !usCheck.valid) return false;
  if (selectedCountry === "CA" && usCheck.valid && !caCheck.valid) return false;

  const nanpParsed = parsePhoneNumberFromString(`+1${national}`);
  if (nanpParsed?.isValid() && nanpParsed.country === selectedCountry) {
    return true;
  }

  // Well-formed NANP when libraries cannot assign area code — reject obvious Indian mobiles.
  if (isIndianMobilePattern(national) && !usCheck.valid && !caCheck.valid) {
    return false;
  }
  return true;
}

export function isValidPhoneForCountry(phone, country = DEFAULT_COUNTRY) {
  const resolved = isSupportedCountry(country) ? country : DEFAULT_COUNTRY;
  const config = getCountryConfig(resolved);
  const national = nationalPhoneForStorage(phone, resolved);
  if (!national) return false;

  const region = config.libPhoneCountry;

  if (isValidNumberForRegion(national, region).valid) {
    if (resolved === "IN") {
      const usValid = isValidNumberForRegion(national, "US").valid;
      const caValid = isValidNumberForRegion(national, "CA").valid;
      if ((usValid || caValid) && !isIndianMobilePattern(national)) {
        return false;
      }
    }
    return true;
  }

  const e164 = `${config.phoneCountryCode}${national}`.replace(/\s/g, "");
  const e164Parsed = parsePhoneNumberFromString(e164);
  if (e164Parsed?.isValid()) {
    if (e164Parsed.country === region) {
      return true;
    }
    if (resolved === "GB" && GB_RELATED_REGIONS.has(e164Parsed.country)) {
      return true;
    }
  }

  const nationalParsed = parsePhoneNumberFromString(national, region);
  if (nationalParsed?.isValid()) {
    if (nationalParsed.country === region) {
      return true;
    }
    if (resolved === "GB" && GB_RELATED_REGIONS.has(nationalParsed.country)) {
      return true;
    }
  }

  if (resolved === "IN") {
    const usValid = isValidNumberForRegion(national, "US").valid;
    const caValid = isValidNumberForRegion(national, "CA").valid;
    if ((usValid || caValid) && !isIndianMobilePattern(national)) {
      return false;
    }
  }

  if (resolved === "US" || resolved === "CA") {
    return isValidNanpForSelectedCountry(national, resolved);
  }

  return false;
}

/**
 * Contact-form validation — accepts well-formed numbers without blocking when the
 * assigned region differs from the selected country code (e.g. US area code + CA).
 */
export function isLenientValidPhoneForCountry(phone, country = DEFAULT_COUNTRY) {
  if (isValidPhoneForCountry(phone, country)) return true;

  const resolved = isSupportedCountry(country) ? country : DEFAULT_COUNTRY;
  const config = getCountryConfig(resolved);
  const national = nationalPhoneForStorage(phone, resolved);
  if (!national) return false;

  if (resolved === "US" || resolved === "CA") {
    if (!isBasicNanpTenDigit(national)) return false;
    if (isIndianMobilePattern(national)) {
      const usValid = isValidNumberForRegion(national, "US").valid;
      const caValid = isValidNumberForRegion(national, "CA").valid;
      if (!usValid && !caValid) return false;
    }
    return (
      isValidNumberForRegion(national, "US").valid ||
      isValidNumberForRegion(national, "CA").valid ||
      Boolean(parsePhoneNumberFromString(`+1${national}`)?.isValid())
    );
  }

  const e164 = `${config.phoneCountryCode}${national}`.replace(/\s/g, "");
  if (parsePhoneNumberFromString(e164)?.isValid()) return true;

  const nationalParsed = parsePhoneNumberFromString(national, config.libPhoneCountry);
  return Boolean(nationalParsed?.isValid());
}

export function validateContactPhoneInput(
  number,
  country = DEFAULT_COUNTRY,
  { allowEmpty = true } = {}
) {
  const trimmed = String(number ?? "").trim();
  if (!trimmed) {
    return allowEmpty ? "" : "Mobile is Required";
  }
  const resolved = isSupportedCountry(country) ? country : DEFAULT_COUNTRY;
  return isLenientValidPhoneForCountry(trimmed, resolved)
    ? ""
    : `Enter a valid ${getCountryConfig(resolved).name} phone number.`;
}

/** Advisory when a number is accepted leniently but may not match the selected country code. */
export function getPhoneRegionMismatchWarning(number, selectedCountry) {
  if (!selectedCountry || !isSupportedCountry(selectedCountry)) return "";
  const national = nationalPhoneForStorage(number, selectedCountry);
  if (!national) return "";
  if (isValidPhoneForCountry(number, selectedCountry)) return "";
  if (!isLenientValidPhoneForCountry(number, selectedCountry)) return "";

  const config = getCountryConfig(selectedCountry);
  const region = config.libPhoneCountry;

  if (selectedCountry === "US" || selectedCountry === "CA") {
    const usCheck = isValidNumberForRegion(national, "US");
    const caCheck = isValidNumberForRegion(national, "CA");
    if (selectedCountry === "US" && caCheck.valid && !usCheck.valid) {
      return `This number may be a Canadian number while ${config.name} is selected as the country code.`;
    }
    if (selectedCountry === "CA" && usCheck.valid && !caCheck.valid) {
      return `This number may be a US number while ${config.name} is selected as the country code.`;
    }
  }

  const e164 = `${config.phoneCountryCode}${national}`.replace(/\s/g, "");
  const e164Parsed = parsePhoneNumberFromString(e164);
  if (e164Parsed?.isValid() && e164Parsed.country && e164Parsed.country !== region) {
    if (selectedCountry === "GB" && GB_RELATED_REGIONS.has(e164Parsed.country)) {
      return "";
    }
    const assignedName = isSupportedCountry(e164Parsed.country)
      ? getCountryConfig(e164Parsed.country).name
      : e164Parsed.country;
    return `This number may belong to ${assignedName} while ${config.name} is selected as the country code.`;
  }

  return `This number may not match the selected country code (${config.name}).`;
}

/**
 * Valid +1 NANP number for US or Canada (Twilio / Firebase legacy callers).
 */
export function isValidNanpSmsNationalMobile(phone) {
  return isValidPhoneForCountry(phone, "US") || isValidPhoneForCountry(phone, "CA");
}

/** India numbers stored as national 10 digits. */
export function isValidIndiaSmsNationalMobile(phone) {
  return isValidPhoneForCountry(phone, "IN");
}

/** Employee/user storage: country-aware or legacy NANP/India. */
export function isValidStoredSmsNationalMobile(phone, country) {
  if (country && isSupportedCountry(country)) {
    return isValidPhoneForCountry(phone, country);
  }
  return (
    isValidNanpSmsNationalMobile(phone) || isValidIndiaSmsNationalMobile(phone)
  );
}

/** Alias kept for SMS callers — means NANP (+1), not US-only. */
export function isValidUsSmsNationalMobile(phone) {
  return isValidNanpSmsNationalMobile(phone);
}

/** SMS-ready: valid for any supported country or legacy NANP/India. */
export function isValidSmsPhone(phone, country, phoneCountryCode) {
  if (country && isSupportedCountry(country)) {
    return isValidPhoneForCountry(phone, country);
  }
  if (phoneCountryCode) {
    const national = String(phone ?? "").replace(/\D/g, "");
    if (!national) return false;
    const e164 = `${phoneCountryCode}${national}`.replace(/\s/g, "");
    const parsed = parsePhoneNumberFromString(e164);
    return Boolean(parsed?.isValid());
  }
  return isValidStoredSmsNationalMobile(phone);
}

export function validatePhoneForCountry(
  number,
  country = DEFAULT_COUNTRY,
  { allowEmpty = true } = {}
) {
  const trimmed = String(number ?? "").trim();
  if (!trimmed) {
    return allowEmpty ? "" : "Mobile is Required";
  }
  return isValidPhoneForCountry(trimmed, country)
    ? ""
    : getInvalidPhoneMessage(country);
}

export function validateNanpSmsMobile(number, { allowEmpty = true } = {}) {
  const tenDigit = validateTenDigitMobile(number, { allowEmpty });
  if (tenDigit) return tenDigit;
  if (!String(number ?? "").trim()) return "";
  return isValidNanpSmsNationalMobile(number) ? "" : INVALID_NANP_MOBILE_MSG;
}

/** Country-aware validation; falls back to legacy when country omitted. */
export function validateStoredSmsMobile(
  number,
  { allowEmpty = true, country, authProfile, companyRecord } = {}
) {
  const resolvedCountry =
    (country && isSupportedCountry(country) ? country : null) ||
    (authProfile || companyRecord
      ? resolveCompanyCountry(authProfile, companyRecord)
      : null);

  if (resolvedCountry && isSupportedCountry(resolvedCountry)) {
    return validatePhoneForCountry(number, resolvedCountry, { allowEmpty });
  }
  const tenDigit = validateTenDigitMobile(number, { allowEmpty });
  if (tenDigit) return tenDigit;
  if (!String(number ?? "").trim()) return "";
  return isValidStoredSmsNationalMobile(number) ? "" : INVALID_STORED_SMS_MOBILE_MSG;
}

/** E.164 list for Twilio from employee notification recipients (legacy + new shape). */
export function phoneRecordsToE164List(records = [], company = {}) {
  const companyContext =
    company?.country || company?.phoneCountryCode ? company : {};
  return [
    ...new Set(
      records
        .map((item) => {
          const { country, phoneCountryCode, phoneNumber } = resolvePhoneFields(
            item,
            companyContext
          );
          if (!phoneNumber) return "";
          if (isValidSmsPhone(phoneNumber, country, phoneCountryCode)) {
            return phoneToValidE164({ country, phoneCountryCode, phoneNumber });
          }
          return "";
        })
        .filter(Boolean)
    ),
  ];
}

/** Human-readable display for national stored numbers (legacy US 10-digit included). */
export function formatPhoneForDisplay(phone, country = DEFAULT_COUNTRY) {
  const national = nationalPhoneForStorage(phone, country);
  if (!national) return "";

  const resolved = isSupportedCountry(country) ? country : DEFAULT_COUNTRY;
  const e164 = phoneToValidE164({
    country: resolved,
    phoneCountryCode: getCountryConfig(resolved).phoneCountryCode,
    phoneNumber: national,
  });
  if (e164) {
    const parsed = parsePhoneNumberFromString(e164);
    if (parsed) {
      return parsed.formatNational();
    }
  }

  // Legacy US 10-digit display
  if (national.length === 10 && resolved === DEFAULT_COUNTRY) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }

  return formatIncompletePhoneNumber(national, resolved) || national;
}

/** Build sendSmsText body fields from a user/contact record. */
export function buildSmsRequestFromRecord(record = {}, company = {}) {
  const { country, phoneCountryCode, phoneNumber } = resolvePhoneFields(record, company);
  if (!phoneNumber) return null;
  return { phoneNumber: [phoneNumber], country, phoneCountryCode };
}

/** E.164 for Firebase Auth `createUser` (Admin SDK requirement). */
export function phoneForFirebaseAuth(phone, country, phoneCountryCode) {
  if (country && isSupportedCountry(country)) {
    const e164 = phoneToValidE164({
      country,
      phoneCountryCode,
      phoneNumber: phone,
    });
    if (e164) return e164;
  }

  // Legacy path
  const d = String(phone ?? "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 11 && d[0] === "1") {
    const n = d.slice(1);
    if (n.length === 10) {
      const parsed = parsePhoneNumberFromString(`+1${n}`);
      if (parsed?.isValid()) return `+1${n}`;
    }
  }
  if (d.length === 10) {
    const us = `+1${d}`;
    const parsedUs = parsePhoneNumberFromString(us);
    if (parsedUs?.isValid()) return us;
    const inMobile = `+91${d}`;
    const parsedIn = parsePhoneNumberFromString(inMobile, "IN");
    if (parsedIn?.isValid()) return inMobile;
    return us;
  }
  const trimmed = String(phone).trim();
  if (trimmed.startsWith("+")) return trimmed.replace(/\s/g, "");
  return `+${d}`;
}
